import { TeamMember } from '@teammood/team';
import { Injectable } from '@angular/core';
import { isBefore, parse, subDays } from "date-fns";

interface FilterDesc {
    id: string;
    name: string;
    predicate: (teamMember: TeamMember) => boolean;
}

export type Filter = FilterDesc & {
    size: number;
    active: boolean;
}

const FILTERS_DESC: FilterDesc[] = [
    {
        id: 'all',
        name: 'all',
        predicate: () => true
    },
    {
        id: 'team members',
        name: 'team_members',
        predicate: (teamMember: TeamMember) => teamMember.roles.indexOf("member") != -1
    },
    {
        id: 'observers',
        name: 'observers',
        predicate: (teamMember: TeamMember) => teamMember.roles.indexOf("observer") != -1
    },
    {
        id: 'managers',
        name: 'managers',
        predicate: (teamMember: TeamMember) => teamMember.roles.indexOf("manager") != -1
    },
    {
        id: 'settings admins',
        name: 'settings_admins',
        predicate: (teamMember: TeamMember) => teamMember.roles.indexOf("admin") != -1
    },
    {
        id: 'analysts',
        name: 'analysts',
        predicate: (teamMember: TeamMember) => teamMember.roles.indexOf("analyst") != -1
    },
    {
        id: 'inactive',
        name: 'inactive',
        predicate: (teamMember: TeamMember) =>
            teamMember.lastMoodDateMessage === "never_gave_mood" &&
            isBefore(parse(teamMember.creationDate, 'dd/MM/yyyy', new Date()), subDays(new Date(), 2))
    },
    {
        id: 'msTeams',
        name: 'msTeams',
        predicate: (teamMember: TeamMember) => teamMember.msTeamsEnabled
    },
    {
        id: 'slack',
        name: 'slack',
        predicate: (teamMember: TeamMember) => teamMember.slackEnabled
    },
    {
        id: 'disabled',
        name: 'disabled',
        predicate: (teamMember: TeamMember) => !teamMember.active
    },
];

function contains(value, term) {
    return value.toLowerCase().indexOf(term.toLowerCase()) !== -1;
}

function tagsContains(tags, term) {
    return tags.filter(function (tag) {
        return tag.name.toLowerCase().indexOf(term.toLowerCase()) !== -1;
    }).length > 0;
}

@Injectable()
export class TeamMembersFilterService {

    applyFilters<T extends TeamMember>(filters: Filter[], teamMembers: T[], fullTextSearch?: string): T[] {
        let filteredTeamMembers = teamMembers;

        filters.forEach(function (filterItem) {
            if (filterItem.active) {
                filteredTeamMembers = filteredTeamMembers.filter(teamMember => filterItem.predicate(teamMember));
            }
        });

        return this.applyFullTextSearch(filteredTeamMembers, fullTextSearch);
    }

    private applyFullTextSearch<T extends TeamMember>(teamMembers: T[], fullTextSearch: string = ''): T[] {
        const search = fullTextSearch.trim();
        return search === '' ? teamMembers :
            teamMembers.filter(teamMember => this.fullTextPredicate(teamMember, fullTextSearch));
    }

    private fullTextPredicate(teamMember: TeamMember, fullTextSearch: string = '') {
        const search = fullTextSearch.trim()
        return search === '' || contains(teamMember.firstname, search) ||
            contains(teamMember.lastname, search) ||
            contains(teamMember.email, search) ||
            contains(teamMember.notificationTime.timeZone, search) ||
            contains(teamMember.roles.join(','), search) ||
            tagsContains(teamMember.tags, search);
    }

    private findFilterById(filters: Filter[], filterId: string) {
        const filter = filters.find((filterItem) => filterItem.id === filterId);
        if (filter) {
            return filter;
        } else {
            throw `filter '${filterId}' not found`;
        }
    }

    toggleFilter(filters: Filter[], filterId: string) {
        const filter = this.findFilterById(filters, filterId);

        if (filter.id === "all") {
            if (!filter.active) {
                filters.map(function (filterItem) {
                    filterItem.active = false;
                });
            }
            filter.active = true;
        }
        else {
            filter.active = !filter.active;

            if (filter.active) {
                this.findFilterById(filters, "all").active = false;
            }
            else {
                // we search if any other button filter is active
                const activeButtonFilters = filters.filter(function (filterItem) {
                    if (filterItem.id !== "all") {
                        return filterItem.active
                    }
                })

                this.findFilterById(filters, "all").active = activeButtonFilters.length === 0;
            }
        }

        return filters;
    }

    getFilters(teamMembers: TeamMember[]): Filter[] {
        return FILTERS_DESC.map((filterDesc) => {
            const size = teamMembers.filter(teamMember => filterDesc.predicate(teamMember)).length
            return {
                id: filterDesc.id,
                name: filterDesc.name,
                active: filterDesc.id === 'all',
                size: size,
                predicate: filterDesc.predicate
            }
        })
    }

    refreshFilters(filters: Filter[], teamMembers: TeamMember[], allCount: number): Filter[] {
        return FILTERS_DESC.map((filterDesc) => {
            const filter = filters.find(f => f.id === filterDesc.id);
            const size = filterDesc.id === 'all' ? allCount : teamMembers.filter(teamMember => filterDesc.predicate(teamMember)).length
            return {
                id: filterDesc.id,
                name: filterDesc.name,
                active: filter.active || false,
                size: size,
                predicate: filterDesc.predicate
            }
        })
    }

}